/*
 * UserAskBlock — Phase 2 structured interaction panel
 *
 * 用户表达自由度改造：
 *   - 全题型支持自定义输入（single_choice 加"其他"选项 → 展开输入框）
 *   - 单题级跳过（提交时 omit qid，复用全局跳过的 wire 契约）
 *   - 日期范围接受自然语言（上周/本月/最近30天/3月1日到15日）
 *   - 默认值改为"AI 推荐"待确认态，避免遮蔽用户真实意图
 *
 * 渲染 user_ask 结构化问题（single_choice / multi_choice / date_range / text /
 * confirm / keyword_select / person_select），收集结构化答案。
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useRef
} from 'react'
import { createPortal } from 'react-dom'
import type {
  MessagePart,
  PickerAnswerValue,
  UserAskQuestion
} from '../../types'
import { ActionSheetPickerContext } from '../UserAskActionSheet'
import {
  parseDateRange,
  deriveDateRangeDefault
} from '../../utils/dateRangeParser'
import { trackClick, readSessionId } from '../../utils/track'
import { useAppDispatch } from '../../../../app/hooks'
import { fetchWatermarkList } from '../../../../actions/photo'
import { DatePicker } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import DepartmentMemberPickerRaw from '../../../components/DepartmentMemberPickerV2'
// DepartmentMemberPickerV2 为强类型组件，这里以宽松 props 调用，包一层 any
const DepartmentMemberPicker = DepartmentMemberPickerRaw as any
import WatermarksPicker from '../../../components/WatermarksPicker'
import './UserAskBlock.scss'

type UserAskPart = Extract<MessagePart, { type: 'user-ask' }>

interface UserAskBlockProps {
  part: UserAskPart
  isLastMessage: boolean
  onSubmit: (response: {
    pending_id: string
    answers: Record<string, any>
  }) => void
  /**
   * 用户已经提交过的答案(从 history 里 [user_ask_response] 消息按 pending_id 反查得到).
   * 非空时切到「结果摘要」视图,直接展示选了什么,不再渲染交互题目.
   */
  submittedAnswers?: Record<string, any>
  /**
   * 展示形态:
   *   - 'all'(默认) — 所有题同屏展开,底部"确认并继续"批量提交.
   *     chat 流 inline 卡片(如「保存模板」)走这种.
   *   - 'step'      — 一次只显 1 题,右上分页器 < N of M >,选完自动 advance,
   *     所有题完成才统一 onSubmit.ActionSheet 弹窗走这种,减轻多题视觉压力.
   */
  presentation?: 'all' | 'step'
}

/** 标识"用户主动选了'其他/自己填'分支" 的特殊 value，仅在 single_choice 内部使用 */
const CUSTOM_OPTION_VALUE = '__custom__'

/** single_choice 里日期范围题的"手动填写"虚拟选项 value.
 *  当题里至少有一个 expansion='date_range' 的选项时,前端自动在 list 末尾
 *  追加这个虚拟选项,选中后渲染纯文本输入框给用户自然语言输入(如"上周").
 *  跟其他 expansion option(如"自定义时间")是互斥关系 — 切换时各自清空对方
 *  的填写状态,避免 wire 上同时带 RangePicker {start,end} 和 NL 文本两份. */
const MANUAL_DATE_VALUE = '__manual_date__'

/** 用户显式点「跳过」后回传给后端/LLM 的占位文案。
 *  不再 omit qid —— 静默缺键会让模型把"没作答"误当默认/全部继续往下猜；
 *  回填这句明确文字，模型与确认摘要都能直接看懂「这题用户主动跳过了」。
 *  注意与「没 default 兜底选第一个」区分：跳过先于 resolveAnswer 命中，不会落默认值。 */
const SKIP_ANSWER_TEXT = '用户跳过选择'

/** 命中这些关键词的选项被视为"自定义"语义，自动展开内联输入 */
const CUSTOM_KEYWORD_RE = /自定义|其他|自由|自己填|自填|手动|手填|手工/

/** 题目语境像在问时间范围（用来推断「自定义」该展开 date_range 还是 text） */
const DATE_CONTEXT_RE = /时间|日期|范围|date|range/i

/**
 * 单选选项 → 埋点 clickItem 映射(右侧 AI 问答面板的"时间范围 Chip" 4 个事件).
 * 按 label/value 关键词推断,匹配不上时返回 null(不上报,避免噪音).
 * 仅在 question 类型是 single_choice 且语境是"时间范围"时调用.
 */
function pickTimeRangeChipTrackItem(opt: {
  value: string
  label?: string
}): string | null {
  const text = `${opt.label || ''} ${opt.value || ''}`.toLowerCase()
  if (/本周|this[_-]?week|thisweek/.test(text)) return 'time_range_this_week'
  if (/上周|last[_-]?week|lastweek/.test(text)) return 'time_range_last_week'
  if (/自定义|custom/.test(text)) return 'time_range_custom'
  if (CUSTOM_KEYWORD_RE.test(text)) return 'time_range_other'
  return null
}

/** 后端 prompt 约定:跨度 > 31 天时提示用户处理可能耗时数分钟.
 *  阈值取 31 是因为大月(1/3/5/7/8/10/12)本身就 31 天,用户选「本月」就会
 *  命中 31 天.阈值 30 会让所有大月触发二次确认,体验上误伤;改 31 之后
 *  单月范围(包括大月)都不触发,跨度真的超过 1 个月才警告.
 */
const LONG_RANGE_THRESHOLD_DAYS = 31

/** 计算 {start, end} 跨度（天数，含首尾），无效时返回 0 */
const computeRangeDays = (val: any): number => {
  if (!val || typeof val !== 'object' || !val.start || !val.end) return 0
  const s = new Date(val.start).getTime()
  const e = new Date(val.end).getTime()
  if (isNaN(s) || isNaN(e) || e < s) return 0
  return Math.floor((e - s) / 86_400_000) + 1
}

/** 推断某个选项被选中时该展开什么内联输入控件。
 *  优先看 option.expands 显式声明；fallback 到关键词启发式。
 *  返回 null 表示该选项不展开任何内联输入。
 */
const inferExpansion = (
  opt: { value: string; label?: string; expands?: 'text' | 'date_range' },
  question: UserAskQuestion
): 'text' | 'date_range' | null => {
  // "手动填写"虚拟选项强制走 text expansion(用户自然语言输入)
  if (opt.value === MANUAL_DATE_VALUE) return 'text'
  if (opt.expands) return opt.expands
  const text = `${opt.label || ''} ${opt.value || ''}`
  if (!CUSTOM_KEYWORD_RE.test(text)) return null
  const ctx = `${question.question} ${question.hint || ''}`
  return DATE_CONTEXT_RE.test(ctx) ? 'date_range' : 'text'
}

/**
 * 计算一道题的「有效默认值」：决定哪个选项默认选中、以及用户没动时提交什么。
 *
 * 分流规则(产品要求)：
 *   - 单题(isMulti=false) → 一律不预选；
 *   - single_choice / confirm → 不论单题/多题、不论有无 default，一律不预选。
 *     原因：这两种题型选了之后会 220ms 自动跳到下一题；预选会让用户进入题目时
 *     就处于"已选"状态被立刻推进，没机会看清选项，体验混乱。confirm 的二元拍板
 *     也不该替用户拍板。
 *   - 多题 multi_choice / keyword_select → 有 question.default 用 default；没有
 *     兜底 [首选项 value]。这两种题不自动跳，预选首项不会引起体验问题。
 *
 * UI 默认高亮与 resolveAnswer 提交共用同一套，保证「看到的预选 = 提交的值」。
 */
const getEffectiveDefault = (q: UserAskQuestion, isMulti: boolean): any => {
  // 单题：一律不预选
  if (!isMulti) return undefined
  // 多题里的 single_choice / confirm：也不预选(避免预选 → 进入即自动跳推进的体验问题)
  if (q.type === 'single_choice' || q.type === 'confirm') return undefined
  const qDefault = (q as { default?: unknown }).default
  if (qDefault !== undefined) return qDefault
  const first = (q.options || [])[0]
  switch (q.type) {
    case 'multi_choice':
    case 'keyword_select':
      return first ? [first.value] : undefined
    default:
      return undefined
  }
}

/** 把 resolveAnswer 的原始值格式化成一行可读摘要（折叠态 head 显示用） */
const formatResolvedAnswer = (
  question: UserAskQuestion,
  value: any
): string => {
  if (value == null || value === '') {
    // picker 题未选 = 查全部, 不是"未填" —— 跟 PickerField 填写态 /
    // SubmittedSummaryView 已提交态的文案保持一致.
    if (question.type === 'picker') {
      return question.picker?.kind === 'watermark' ? '全部水印' : '全部成员'
    }
    return '未填'
  }
  const optLabel = (raw: any): string => {
    const opt = (question.options || []).find((o) => o.value === raw)
    return opt?.label || String(raw)
  }
  // picker value: { kind, ...ids, summary } —— 直接读 summary, 不复述 id.
  // 必须放在数组判断 / date_range 判断之前, 避免被错分支提前 return.
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof value.summary === 'string'
  ) {
    return value.summary || '未选择'
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '未选择' : value.map(optLabel).join('、')
  }
  if (typeof value === 'boolean') return value ? '是' : '否'
  if (
    value &&
    typeof value === 'object' &&
    typeof value.start === 'string' &&
    typeof value.end === 'string'
  ) {
    return `${value.start} 至 ${value.end}`
  }
  return optLabel(value)
}

/** 已提交摘要视图 —— 抽成独立子组件, 避免主组件因为早期 return 触发
 *  react-hooks/rules-of-hooks (hooks 必须无条件调用). */
const SubmittedSummaryView: React.FC<{
  part: UserAskPart
  submittedAnswers: Record<string, any>
}> = ({ part, submittedAnswers }) => (
  <div className="aiClerk-ua aiClerk-ua--summary">
    {part.title && (
      <div className="aiClerk-ua__summary-title">{part.title}</div>
    )}
    <div className="aiClerk-ua__summary-rows">
      {part.questions.map((q) => {
        const value = submittedAnswers[q.qid]
        const label = q.label_short || q.question
        // 显式跳过 → value 已是 SKIP_ANSWER_TEXT，formatResolvedAnswer 原样透出。
        // value 缺失(取消本轮 / 历史旧消息)统一回显「已跳过」——不再让 picker
        // 空值错回显成「全部水印/全部成员」(那是"查全部"语义，不是跳过)。
        const display =
          value === undefined ? '已跳过' : formatResolvedAnswer(q, value)
        return (
          <div className="aiClerk-ua__summary-row" key={q.qid}>
            <span className="aiClerk-ua__summary-label">{label}</span>
            <span className="aiClerk-ua__summary-value">{display}</span>
          </div>
        )
      })}
    </div>
  </div>
)

const UserAskBlock: React.FC<UserAskBlockProps> = ({
  part,
  isLastMessage,
  onSubmit,
  submittedAnswers,
  presentation = 'all'
}) => {
  // step 模式下"当前正在答的题号 idx".'all' 模式不用,留 0 兜底.
  // 分页器 < N of M > 通过 setCurrentIdx 操作,N = currentIdx + 1.
  const [currentIdx, setCurrentIdx] = useState(0)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [submitted, setSubmitted] = useState(part.submitted ?? false)
  const [customInputs, setCustomInputs] = useState<Record<string, string>>({})
  /** 当 single_choice 选了带 expansion 的选项后，用户在内联输入控件里填的值（按 qid 索引）。
   *  类型可能是 string（text expansion）或 { start, end }（date_range expansion）。 */
  const [expansionValues, setExpansionValues] = useState<Record<string, any>>(
    {}
  )
  const [skipped, setSkipped] = useState<Record<string, boolean>>({})
  // 题型主体 UI 之外的"手动输入"兜底文本(multi_choice/keyword_select/picker/
  // date_range/confirm 通用).用户在此填了文本 → resolveAnswer 按题型转换 wire 形态.
  // single_choice 走另一套"虚拟选项 MANUAL_DATE_VALUE",不进这个 state.
  const [manualInputs, setManualInputs] = useState<Record<string, string>>({})

  // picker 题:用户是否"主动点过全部成员/全部水印"(按 qid 存)。
  // picker 的"全部"在协议上是 undefined(空 ids = 查全部),光看 answers 区分不了
  // "没动过的默认"和"主动选了全部",所以单独记一个 flag 驱动选中态视觉。
  // 提到父组件(而非 PickerField 本地 state)是因为:数据最后统一提交、用户会用
  // 分页器翻回去 review,step 模式翻页会 remount 子组件,本地 state 会丢 → 选中态
  // 回退成"AI 推荐"虚线,让用户误以为选择被改了。放父组件按 qid 存才能跨翻页保持。
  const [pickerAllConfirmed, setPickerAllConfirmed] = useState<
    Record<string, boolean>
  >({})

  // 用户切到 date_range expansion(典型场景:single_choice 选「自定义」)时,
  // 把 AI 推荐(question.default → 对应 option label / token)推断出的实际
  // 日期范围预填进 expansionValues — 比让 picker 留空让用户从头点更友好.
  //
  // ⚠ 选中判定要用"实际生效值":
  //   - 用户动过 → answers[qid]
  //   - 用户没动 → q.default(AI 推荐.UI 上"自定义" chip 也已经勾选展开)
  //   光看 answers 会漏掉"AI default=custom + 用户还没点"的场景,这正是
  //   后端 prompt 例子里常见的"AI 推荐自定义但还不知具体范围"用例.
  //
  // 兜底策略:
  //   - default 是已知 token / 中文表达("last_month" / "上月")→ 推断实际日期
  //   - default 就是 "custom" 或推断不出 → 用「上月」做最终兜底
  //     (AI 推"自定义"= AI 也不确定用户想要啥,前端给安全起点比留空友好)
  //
  // 只在 expansionValues[qid] 还没值时填,且只填一次(用户改/清空后不再
  // 覆盖,尊重用户操作).
  useEffect(() => {
    let next: Record<string, any> | null = null
    for (const q of part.questions) {
      if (q.type !== 'single_choice') continue
      const qDefault = (q as { default?: unknown }).default
      const selected = answers[q.qid] !== undefined ? answers[q.qid] : qDefault
      if (typeof selected !== 'string') continue
      const opt = (q.options || []).find((o) => o.value === selected)
      if (!opt) continue
      if (inferExpansion(opt, q) !== 'date_range') continue
      if (expansionValues[q.qid]) continue
      const fallback =
        deriveDateRangeDefault(qDefault, q.options) || parseDateRange('上月')
      if (!fallback) continue
      if (!next) next = {}
      next[q.qid] = fallback
    }
    if (next) {
      setExpansionValues((prev) => ({ ...prev, ...next }))
    }
  }, [answers, expansionValues, part.questions])

  // 多 question 折叠:
  //   - 活跃卡片 → 默认全部展开,让用户一次看到所有题目(不用挨个点开)
  //   - 历史 / 已提交卡片(disabled)→ 初始全部折叠,紧凑展示,点 head 可逐个展开
  const [expandedQids, setExpandedQids] = useState<Set<string>>(() => {
    const initiallyDisabled = (part.submitted ?? false) || !isLastMessage
    if (initiallyDisabled) return new Set()
    return new Set(part.questions.map((q) => q.qid).filter(Boolean))
  })
  const toggleExpand = useCallback((qid: string) => {
    setExpandedQids((prev) => {
      const next = new Set(prev)
      if (next.has(qid)) next.delete(qid)
      else next.add(qid)
      return next
    })
  }, [])
  /** 自动填写预览默认折叠，避免占空间；用户点 head 展开看明细 */
  const [previewExpanded, setPreviewExpanded] = useState(false)
  /** 长跨度二次确认态：超过 30 天时第一次点提交触发，二次点击才真提交 */
  const [confirmingLongRange, setConfirmingLongRange] = useState(false)

  const disabled = submitted || !isLastMessage

  const updateAnswer = useCallback(
    (qid: string, value: any) => {
      setAnswers((prev) => ({ ...prev, [qid]: value }))
      // 用户主动操作 → 取消跳过态
      setSkipped((prev) => (prev[qid] ? { ...prev, [qid]: false } : prev))

      // single_choice 选项互斥清空 expansion 旧值:
      //   - "自定义时间"(expansion=date_range) ↔ "手动填写"(MANUAL_DATE_VALUE
      //     强制 expansion=text)是两种不同 expansion 输入,切换时旧值类型不再
      //     匹配,要清掉,避免 wire 同时带 {start,end} 对象和 NL 字符串.
      //   - 切到不展开 expansion 的普通 option(本周/上周/...)也清掉,免得
      //     resolveAnswer 误把旧的 expansion 值当作当前答案传出去.
      const q = part.questions[currentIdx]
      if (q && q.qid === qid && q.type === 'single_choice') {
        const optMeta =
          value === MANUAL_DATE_VALUE
            ? ({ value: MANUAL_DATE_VALUE } as {
                value: string
                label?: string
                expands?: 'text' | 'date_range'
              })
            : (q.options || []).find((o) => o.value === value) || null
        const newExp = optMeta ? inferExpansion(optMeta, q) : null
        setExpansionValues((prev) => {
          const oldVal = prev[qid]
          const oldType =
            typeof oldVal === 'string'
              ? 'text'
              : oldVal && typeof oldVal === 'object'
                ? 'date_range'
                : null
          // 类型不匹配 → 清空(let useEffect re-fill default if needed)
          if (oldType && oldType !== newExp) {
            const next = { ...prev }
            delete next[qid]
            return next
          }
          return prev
        })
      }

      // step 模式 auto-advance:只在用户主动 onChange 时触发,**不**靠 useEffect
      // 监听 answers — 否则用户用分页器切回上一题,answers[qid] 已有值,
      // useEffect 又会自动跳到下一个(死循环 bug).
      if (presentation !== 'step') return
      if (!q || q.qid !== qid) return
      // 最后一题选完不自动提交 — 只显示选中态,等用户手动点"完成并提交".
      // 提交是终点操作,误点的代价高,必须显式按按钮确认.
      const isLastQ = currentIdx === part.questions.length - 1
      if (isLastQ) return
      // 自动跳转(产品要求，减少每题都点「下一个」的麻烦):
      //   - single_choice：选了普通选项 → 自动跳；选了会展开输入的项(自定义 / 手动填写
      //     / 带 expansion)→ 不跳，用户要继续输入，由底部「下一个」手动推进
      //   - confirm(二元拍板)：点了【是/否】→ 自动跳(单题 confirm 是末题，上面已 return，
      //     交给 isSingleConfirm 的 useEffect 直接提交)
      //   - picker(选择类) / 多选 / keyword / 文本 / 日期(输入类) → 不自动跳
      if (q.type === 'single_choice') {
        const opt = (q.options || []).find((o) => o.value === value)
        const opensInput =
          value === MANUAL_DATE_VALUE ||
          value === CUSTOM_OPTION_VALUE ||
          (opt ? inferExpansion(opt, q) !== null : false)
        if (!opensInput) setTimeout(() => advanceStepRef.current(), 220)
        return
      }
      if (q.type === 'confirm') {
        setTimeout(() => advanceStepRef.current(), 220)
        return
      }
    },
    [presentation, part.questions, currentIdx]
  )

  const sheetCtxForSkip = useContext(ActionSheetPickerContext)
  const toggleSkip = useCallback(
    (qid: string) => {
      setSkipped((prev) => {
        const next = !prev[qid]
        // 仅"启用跳过"上报(取消跳过不算 skip_question 语义)
        if (next) {
          // spec: skip_question 不带 string1(只 ai_fill/send_message 带)
          trackClick('formFilling', 'skip_question', {
            requirement_id: 1103,
            track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
            sessionID: readSessionId()
          })
          // ActionSheet 场景再补一次 ask_skip (带 pendingId + 距 show ms),
          // chat inline 场景 Context 为 null 跳过 (那种没有 ask_show 配套).
          if (sheetCtxForSkip) {
            sheetCtxForSkip.reportAskSkip()
          }
        }
        return { ...prev, [qid]: next }
      })
    },
    [sheetCtxForSkip]
  )

  /** 取得提交时的有效答案：用户动过 → 用户值，未动过 → AI 默认推荐
   *
   * 注:expansion 分支(date_range / text)对"用户动过"和"AI 默认 + 没动"
   *    两种场景都生效 — 因为前端会在用户点 expansion option 时自动 fill
   *    expansionValues(AI 推荐 default=custom + 用户没点的场景也会触发).
   *    旧版只在 userValue !== undefined 时跑 expansion 分支,导致 AI 推
   *    "自定义"但用户没动时,提交回去的是 "custom" 字符串而不是 fill 的
   *    日期范围对象 — 后端只能看到 effective="custom",无法消费.
   */
  const resolveAnswer = useCallback(
    (q: UserAskQuestion): any => {
      const userValue = answers[q.qid]
      // 用户动过 → 用户值;没动 → 兜底用「有效默认值」(question.default,没有则第一个选项)
      const effective =
        userValue !== undefined
          ? userValue
          : getEffectiveDefault(q, part.questions.length > 1)

      // 「手动输入」兜底:multi_choice/keyword_select/picker/date_range/confirm
      // 题在主 UI 下方有 manual 文本输入,用户填了 → 按题型转 wire 形态.
      // single_choice 走另一套虚拟选项(MANUAL_DATE_VALUE),不进这个分支.
      const manualText = (manualInputs[q.qid] || '').trim()
      if (manualText) {
        if (q.type === 'multi_choice' || q.type === 'keyword_select') {
          // 用户已选数组 + manual 文本作为额外一项
          const arr = Array.isArray(effective) ? [...effective] : []
          if (!arr.includes(manualText)) arr.push(manualText)
          return arr
        }
        if (q.type === 'picker') {
          const kind: 'watermark' | 'department_member' =
            q.picker?.kind === 'watermark' ? 'watermark' : 'department_member'
          if (kind === 'watermark') {
            return { kind, watermark_ids: [], summary: manualText }
          }
          return {
            kind,
            user_ids: [],
            department_ids: [],
            summary: manualText
          }
        }
        if (q.type === 'date_range') {
          // 手动输入直接传原文,自然语言时间交后端 AI 解析。前端不再
          // parseDateRange——它只覆盖 ~80% 常见表达,且会把"最近4天"这类
          // 原始语义提前换算成具体日期、丢掉用户原话。
          return manualText
        }
        if (q.type === 'confirm') {
          // confirm 协议层是 boolean,manual 文本覆盖时只能传 string,后端容忍
          return manualText
        }
      }

      // picker 解析与 effective/qDefault 解耦 —— 只看用户实际选了什么:
      //   选了具体项(value 是带非空 ids 的对象)→ 原样回传
      //   否则(没动 / 点了"全部"→ value=undefined / 只有 qDefault)→ 一律显式回传
      //   "全部水印 / 全部成员"对象(空 ids + summary).保证选"全部"时后端永远收到
      //   明确的"全部"语义:不依赖 qid 缺席或 qDefault 推断,也不会被 qDefault 顶掉.
      if (q.type === 'picker') {
        const kind: 'watermark' | 'department_member' =
          q.picker?.kind === 'watermark' ? 'watermark' : 'department_member'
        const v = userValue
        const hasIds =
          !!v &&
          typeof v === 'object' &&
          ((Array.isArray(v.watermark_ids) && v.watermark_ids.length > 0) ||
            (Array.isArray(v.user_ids) && v.user_ids.length > 0) ||
            (Array.isArray(v.department_ids) && v.department_ids.length > 0))
        if (hasIds) return v
        return kind === 'watermark'
          ? { kind, watermark_ids: [], summary: '全部水印' }
          : { kind, user_ids: [], department_ids: [], summary: '全部成员' }
      }

      if (effective === undefined) {
        if (q.type === 'multi_choice' || q.type === 'keyword_select') return []
        if (q.type === 'confirm') return false
        return undefined
      }
      // single_choice 选了"其他（自己填）" → 用自定义文本
      if (q.type === 'single_choice' && effective === CUSTOM_OPTION_VALUE) {
        const custom = (customInputs[q.qid] || '').trim()
        return custom || undefined
      }
      // single_choice 选了"手动输入"(MANUAL_DATE_VALUE):用户输入的文本存在
      // expansionValues[qid].一律传原文字符串——时间类表达(如"最近4天")交后端
      // AI 解析,前端不再 parseDateRange 提前换算,保留用户原话语义。
      // (RangePicker 选具体日期是另一条分支,仍传 {start,end} 结构。)
      if (q.type === 'single_choice' && effective === MANUAL_DATE_VALUE) {
        const text =
          typeof expansionValues[q.qid] === 'string'
            ? (expansionValues[q.qid] as string).trim()
            : ''
        if (!text) return undefined
        return text
      }
      // single_choice 选了带 expansion 的选项 → 优先用内联输入的值.
      // 对"用户动过"和"AI 默认 + 没动"都生效:用户切到 expansion option 后
      // useEffect 会自动 fill expansionValues,这里读到 fill 的值就回传.
      if (q.type === 'single_choice' && typeof effective === 'string') {
        const selectedOpt = (q.options || []).find((o) => o.value === effective)
        if (selectedOpt) {
          const expansion = inferExpansion(selectedOpt, q)
          const expVal = expansionValues[q.qid]
          if (expansion === 'text' && typeof expVal === 'string') {
            const trimmed = expVal.trim()
            if (trimmed) return trimmed
          }
          if (
            expansion === 'date_range' &&
            expVal &&
            typeof expVal === 'object' &&
            expVal.start &&
            expVal.end
          ) {
            return expVal
          }
          // expansion 已展开但 expansionValues 还没值(useEffect 还没跑完
          // 的瞬间)→ 用 option.value 兜底,后端至少能看到 "custom" 知道
          // 用户选了自定义,虽然不知具体日期
        }
      }
      return effective
    },
    [
      answers,
      customInputs,
      expansionValues,
      manualInputs,
      part.questions.length
    ]
  )

  /** 计算所有题目里最长的日期范围跨度（天数），用于触发"大跨度二次确认" */
  const longestRangeDays = useMemo(() => {
    let max = 0
    for (const q of part.questions) {
      if (skipped[q.qid]) continue
      const resolved = resolveAnswer(q)
      max = Math.max(max, computeRangeDays(resolved))
    }
    return max
  }, [part.questions, skipped, resolveAnswer])

  const isLongRange = longestRangeDays > LONG_RANGE_THRESHOLD_DAYS

  const handleSubmit = useCallback(() => {
    // 幂等防护：单题 confirm 同时有 useEffect 自动 submit + 用户手动点「提交」按钮，
    // 两条路径都调 handleSubmit；submitted=true 后再调用直接 return，避免双发 onSubmit。
    if (submitted) return
    // 用户点击"确认并继续" click 埋点(spec 不带 string1)
    trackClick('formFilling', 'confirm_continue', {
      requirement_id: 1103,
      track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
      sessionID: readSessionId()
    })
    // 第一次点提交且涉及大跨度 → 进入二次确认态,本轮不真正提交
    if (isLongRange && !confirmingLongRange) {
      setConfirmingLongRange(true)
      return
    }
    const finalAnswers: Record<string, any> = {}
    for (const q of part.questions) {
      // 单题跳过 → 回填「用户跳过选择」占位文案（不再 omit qid）：
      // 让后端/LLM 明确看到这题被主动跳过，而不是缺键去猜默认/全部。
      if (skipped[q.qid]) {
        finalAnswers[q.qid] = SKIP_ANSWER_TEXT
        continue
      }

      const resolved = resolveAnswer(q)

      // 多选/关键词题：合并自定义输入到数组
      if (
        (q.type === 'multi_choice' || q.type === 'keyword_select') &&
        q.allow_custom &&
        customInputs[q.qid]
      ) {
        const custom = customInputs[q.qid].trim()
        if (custom) {
          const arr = Array.isArray(resolved) ? [...resolved] : []
          if (!arr.includes(custom)) arr.push(custom)
          finalAnswers[q.qid] = arr
          continue
        }
      }

      if (resolved !== undefined) {
        finalAnswers[q.qid] = resolved
      }
    }
    setSubmitted(true)
    onSubmit({ pending_id: part.pendingId, answers: finalAnswers })
  }, [
    onSubmit,
    part.pendingId,
    part.questions,
    skipped,
    resolveAnswer,
    customInputs,
    isLongRange,
    confirmingLongRange
  ])

  // 注：原「采纳 AI 推荐」按钮已在远程 e2fc5cbe 移除，对应的
  // handleAcceptAllAI 处理函数随之删除，避免留下未引用的死代码。

  // ─── step 模式 advance 控制 ──────────────────────────────────
  // 当前题答完 → 推到下一个;最后一题答完 → 调 handleSubmit 统一提交.
  // 跳过(skip)也走同一 advance,只是 finalAnswers 里 omit 该 qid.
  const advanceStep = useCallback(() => {
    if (currentIdx >= part.questions.length - 1) {
      handleSubmit()
    } else {
      setCurrentIdx((i) =>
        Math.min(i + 1, Math.max(0, part.questions.length - 1))
      )
    }
  }, [currentIdx, part.questions.length, handleSubmit])

  // advanceStep 通过 ref 透传给 updateAnswer,避免 stale closure
  // (updateAnswer 的 useCallback deps 太多会失去 memo 意义)
  const advanceStepRef = useRef(advanceStep)
  useEffect(() => {
    advanceStepRef.current = advanceStep
  }, [advanceStep])

  // 二元拍板形态: 单 question + type=confirm. 渲染双按钮【是】【否】, 点击即提交, 不走全局 actions.
  // 协议层 type=confirm 即"二元拍板"语义 (openclaw user-ask-tool 已对齐), LLM 把 yes/no 场景显式标 type=confirm.
  // ⚠️ 必须放在 early return 之前 — useEffect 不能位于条件 return 之后, 否则 hook 顺序错位触发白屏.
  const isSingleConfirm =
    !disabled &&
    part.questions.length === 1 &&
    (part.questions[0] as any).type === 'confirm'

  // 二元拍板: 用户点【是】/【否】后立即 submit
  React.useEffect(() => {
    if (!isSingleConfirm || submitted) return
    const q = part.questions[0]
    if (answers[q.qid] !== undefined) {
      handleSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleConfirm, answers, submitted])

  // 已提交摘要视图:有 submittedAnswers 就直接展示用户选了什么,跳过交互态.
  // 历史卡片刷新后也能还原 (answers 持久化在 [user_ask_response] 用户消息里).
  // 必须放在所有 hooks 之后, 避免 react-hooks/rules-of-hooks 报"条件 hooks".
  if (submittedAnswers) {
    return (
      <SubmittedSummaryView part={part} submittedAnswers={submittedAnswers} />
    )
  }

  // ─── step 模式渲染分支 ────────────────────────────────────────
  // 一次只显示当前 question;右上分页器可手切;选项类自动 advance;
  // 文本/多选/picker 等"非点即提"类型用底部"下一个"按钮显式推进.
  if (presentation === 'step' && !disabled && part.questions.length > 0) {
    const total = part.questions.length
    const safeIdx = Math.min(Math.max(0, currentIdx), total - 1)
    const q = part.questions[safeIdx]
    const isCurrentSkipped = !!skipped[q.qid]
    const isLastQuestion = safeIdx === total - 1
    return (
      <div
        className={`aiClerk-ua aiClerk-ua--step${
          disabled ? ' is-disabled' : ''
        }${isLastQuestion ? ' is-last-question' : ''}`}
      >
        {/* 头部:左标题 + 右分页器 */}
        <div className="aiClerk-ua__step-head">
          <div className="aiClerk-ua__step-head-left">
            {part.title && (
              <div className="aiClerk-ua__title">{part.title}</div>
            )}
            {part.description && (
              <div className="aiClerk-ua__desc">{part.description}</div>
            )}
          </div>
          {total > 1 && (
            <div className="aiClerk-ua__step-pager" aria-label="题目分页">
              <button
                type="button"
                className="aiClerk-ua__step-pager-btn"
                onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                disabled={safeIdx === 0}
                aria-label="上一题"
              >
                ‹
              </button>
              <span className="aiClerk-ua__step-pager-num">
                <span className="aiClerk-ua__step-pager-cur">
                  {safeIdx + 1}
                </span>
                {` / ${total}`}
              </span>
              <button
                type="button"
                className="aiClerk-ua__step-pager-btn"
                onClick={() => setCurrentIdx((i) => Math.min(total - 1, i + 1))}
                disabled={safeIdx === total - 1}
                aria-label="下一个"
              >
                ›
              </button>
            </div>
          )}
        </div>

        {/* Auto-fill preview(共用) */}
        {part.autoFillPreview && part.autoFillPreview.length > 0 && (
          <div className="aiClerk-ua__preview">
            <button
              type="button"
              className="aiClerk-ua__preview-head"
              onClick={() => setPreviewExpanded((v) => !v)}
              aria-expanded={previewExpanded}
            >
              <span className="aiClerk-ua__preview-head-icon">✓</span>
              已自动识别 {part.autoFillPreview.length} 项
              <span
                className={`aiClerk-ua__preview-chevron${
                  previewExpanded ? ' is-expanded' : ''
                }`}
              >
                ⌃
              </span>
            </button>
            {previewExpanded && (
              <div className="aiClerk-ua__preview-list">
                {part.autoFillPreview.map((item, i) => (
                  <div className="aiClerk-ua__preview-item" key={i}>
                    <span className="aiClerk-ua__preview-label">
                      {item.label}
                    </span>
                    <span className="aiClerk-ua__preview-value">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 当前题主体 */}
        <div className="aiClerk-ua__step-body">
          <div className="aiClerk-ua__field-label">
            {q.question}
            {(q.type === 'multi_choice' || q.type === 'keyword_select') && (
              <span className="aiClerk-ua__multi-tag">可多选</span>
            )}
          </div>
          {q.hint && <div className="aiClerk-ua__field-hint">{q.hint}</div>}
          {/* picker 已选摘要已合并进 picker-add 按钮文案"已选(xxx 等 N 项)",
              不再在这里渲染单独 summary 行.清空入口由 picker-all 按钮("全部成员"
              点击回默认)承担. */}
          {isCurrentSkipped ? (
            <div className="aiClerk-ua__skip-hint">已跳过此题,不会提交</div>
          ) : (
            <QuestionInput
              key={q.qid}
              question={q}
              value={answers[q.qid]}
              customText={customInputs[q.qid] || ''}
              expansionValue={expansionValues[q.qid]}
              onChange={(val) => updateAnswer(q.qid, val)}
              onCustomChange={(text) =>
                setCustomInputs((prev) => ({ ...prev, [q.qid]: text }))
              }
              onExpansionChange={(val) =>
                setExpansionValues((prev) => ({ ...prev, [q.qid]: val }))
              }
              disabled={disabled}
              multiQuestion={part.questions.length > 1}
              manualInput={manualInputs[q.qid] || ''}
              onManualChange={(text) =>
                setManualInputs((prev) => ({ ...prev, [q.qid]: text }))
              }
              pickerAllConfirmed={!!pickerAllConfirmed[q.qid]}
              onPickerAllConfirmedChange={(v) =>
                setPickerAllConfirmed((prev) => ({ ...prev, [q.qid]: v }))
              }
              onPickerAllSelected={
                // picker 点「全部水印/全部成员」= 用户明确确认"查全部" → 自动跳下一题。
                // 末题不跳(只剩「提交」)；非末题 220ms 后 advance，跟 single_choice 的
                // 自动跳节奏一致。
                isLastQuestion
                  ? undefined
                  : () => setTimeout(() => advanceStepRef.current(), 220)
              }
            />
          )}
        </div>

        {/* 底部 actions:跳过(中间题)+ 下一个/完成并提交(非选项类).
            顺序:跳过在左,主按钮在右 — flex justify-content: flex-end 整体右对齐.
            ⚠ 历史教训：曾经做过"选了普通选项就藏按钮 + 220ms 自动跳"，导致 single_choice
              下面不展示「下一题/提交」，用户无法推进的严重 bug。同样的坑：单题 confirm
              曾经排除掉这套 actions(走「双按钮即提交」)，但底部还有手动输入块，用户填了
              文本但没点【是/否】就找不到按钮。现在按钮永远显示，单题 confirm 也走「提交」
              按钮兜底；点【是/否】仍触发自动 submit(handleSubmit 已加幂等防护)。 */}
        <div className="aiClerk-ua__step-actions">
          {/* 跳过：仅非末题显示 —— 末题只留「提交」（整轮不想填可点右上角关闭取消）。
                跳过 = omit 当前题，立即推进下一个/提交。 */}
          {!isLastQuestion && (
            <button
              type="button"
              className={`aiClerk-ua__skip-link${
                isCurrentSkipped ? ' is-active' : ''
              }`}
              onClick={() => {
                toggleSkip(q.qid)
                if (!isCurrentSkipped) {
                  // 让 toggleSkip 的 state 先 flush 再 advance — 用微任务延后
                  setTimeout(advanceStep, 0)
                }
              }}
            >
              {isCurrentSkipped ? '取消跳过' : '跳过'}
            </button>
          )}
          {!isCurrentSkipped && (
            <button
              type="button"
              className="aiClerk-ua__btn aiClerk-ua__btn--primary"
              onClick={advanceStep}
            >
              {isLastQuestion ? '提交' : '下一个'}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`aiClerk-ua${disabled ? ' is-disabled' : ''}`}>
      {part.title && <div className="aiClerk-ua__title">{part.title}</div>}
      {part.description && (
        <div className="aiClerk-ua__desc">{part.description}</div>
      )}

      {/* Auto-fill preview — 默认折叠，点击展开 */}
      {part.autoFillPreview && part.autoFillPreview.length > 0 && (
        <div className="aiClerk-ua__preview">
          <button
            type="button"
            className="aiClerk-ua__preview-head"
            onClick={() => setPreviewExpanded((v) => !v)}
            aria-expanded={previewExpanded}
          >
            <span className="aiClerk-ua__preview-head-icon">✓</span>
            已自动识别 {part.autoFillPreview.length} 项
            <span
              className={`aiClerk-ua__preview-chevron${
                previewExpanded ? ' is-expanded' : ''
              }`}
            >
              ⌃
            </span>
          </button>
          {previewExpanded && (
            <div className="aiClerk-ua__preview-list">
              {part.autoFillPreview.map((item, i) => (
                <div className="aiClerk-ua__preview-item" key={i}>
                  <span className="aiClerk-ua__preview-label">
                    {item.label}
                  </span>
                  <span className="aiClerk-ua__preview-value">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Questions */}
      {part.questions.length > 0 && (
        <div className="aiClerk-ua__questions">
          {part.questions.map((q) => {
            const isSkipped = !!skipped[q.qid]
            // 单 question 活跃卡片直接展开;多 question / disabled 卡片按
            // expandedQids 控制(disabled 卡片初始全折叠,见上面的 useState)
            const expanded =
              expandedQids.has(q.qid) ||
              (!disabled && part.questions.length === 1)
            // head 是否可点击切换:多 question 永远可点;disabled 卡片即使单题
            // 也可点(历史卡片折叠后需要能展开查看明细)
            const headClickable = part.questions.length > 1 || disabled
            // 折叠态答案摘要:仅在"活跃卡片"或"用户本次确实动过这题"时显示,
            // 避免历史卡片把 AI 默认值误当成用户答案展示(hydration 不带用户答案)
            const touched = answers[q.qid] !== undefined
            const answerSummary =
              !expanded && (!disabled || touched || isSkipped)
                ? isSkipped
                  ? '已跳过'
                  : formatResolvedAnswer(q, resolveAnswer(q))
                : ''
            return (
              <div
                className={`aiClerk-ua__field${isSkipped ? ' is-skipped' : ''}${
                  expanded ? ' is-expanded' : ' is-collapsed'
                }`}
                key={q.qid}
              >
                <div
                  className="aiClerk-ua__field-head"
                  onClick={
                    headClickable ? () => toggleExpand(q.qid) : undefined
                  }
                  role={headClickable ? 'button' : undefined}
                  tabIndex={headClickable ? 0 : undefined}
                >
                  {headClickable && (
                    <span
                      className={`aiClerk-ua__field-caret${
                        expanded ? ' is-open' : ''
                      }`}
                      aria-hidden
                    >
                      ▸
                    </span>
                  )}
                  <div className="aiClerk-ua__field-label">
                    {q.question}
                    {(q.type === 'multi_choice' ||
                      q.type === 'keyword_select') && (
                      <span className="aiClerk-ua__multi-tag">可多选</span>
                    )}
                  </div>
                  {answerSummary && (
                    <span
                      className={`aiClerk-ua__field-answer${
                        isSkipped ? ' is-skipped' : ''
                      }`}
                      title={answerSummary}
                    >
                      {answerSummary}
                    </span>
                  )}
                </div>
                {expanded && q.hint && (
                  <div className="aiClerk-ua__field-hint">{q.hint}</div>
                )}
                {expanded && !isSkipped && (
                  <QuestionInput
                    question={q}
                    value={answers[q.qid]}
                    customText={customInputs[q.qid] || ''}
                    expansionValue={expansionValues[q.qid]}
                    onChange={(val) => updateAnswer(q.qid, val)}
                    onCustomChange={(text) =>
                      setCustomInputs((prev) => ({ ...prev, [q.qid]: text }))
                    }
                    onExpansionChange={(val) =>
                      setExpansionValues((prev) => ({ ...prev, [q.qid]: val }))
                    }
                    disabled={disabled}
                    multiQuestion={part.questions.length > 1}
                  />
                )}
                {expanded && isSkipped && (
                  <div className="aiClerk-ua__skip-hint">
                    已跳过此题，不会提交
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Actions — 二元拍板形态(单 question + type=confirm)隐藏整组按钮,
          点【是】/【否】 auto-submit (见上方 useEffect). 其他场景照常显示. */}
      {!disabled && !isSingleConfirm && (
        <>
          {confirmingLongRange && (
            <div className="aiClerk-ua__warn">
              <span className="aiClerk-ua__warn-icon">⚠️</span>
              <span className="aiClerk-ua__warn-text">
                您选的时间范围跨度 <b>{longestRangeDays} 天</b>
                ，处理可能耗时数分钟。再次点击「确认提交」继续。
              </span>
            </div>
          )}
          <div className="aiClerk-ua__actions">
            <button
              type="button"
              className={`aiClerk-ua__btn aiClerk-ua__btn--primary${
                confirmingLongRange ? ' aiClerk-ua__btn--warn' : ''
              }`}
              onClick={handleSubmit}
            >
              {confirmingLongRange
                ? `确认提交（${longestRangeDays} 天）`
                : '确认并继续'}
            </button>
            {confirmingLongRange && (
              <button
                type="button"
                className="aiClerk-ua__btn aiClerk-ua__btn--ghost"
                onClick={() => setConfirmingLongRange(false)}
              >
                返回修改
              </button>
            )}
            {/* 跳过此题: 作用于当前最近展开的那道题. 多题展开取插入顺序最后一个;
                没有任何展开题时隐藏 (没有可跳过的对象). 弱化形态放主按钮之后. */}
            {(() => {
              const activeQid = Array.from(expandedQids).slice(-1)[0]
              if (!activeQid) return null
              const activeSkipped = !!skipped[activeQid]
              return (
                <button
                  type="button"
                  className={`aiClerk-ua__skip-link${
                    activeSkipped ? ' is-active' : ''
                  }`}
                  onClick={() => toggleSkip(activeQid)}
                >
                  {activeSkipped ? '取消跳过' : '跳过此题'}
                </button>
              )
            })()}
          </div>
        </>
      )}

      {submitted && <div className="aiClerk-ua__submitted">✓ 已提交</div>}
      {/* 非最后一条且未提交:这次提问已被后续对话取代。给个明确的"已结束"
          指示,不再是一个没有任何状态说明的灰色表单。措辞中性 —— 不断言
          "未作答"(submitted 依赖后端持久化,hydration 不一定可靠)。 */}
      {disabled && !submitted && (
        <div className="aiClerk-ua__closed">· 此提问已结束</div>
      )}
    </div>
  )
}

// --- Per-question input controls ---

/**
 * "手动输入"选项的共享组件 — 用户在选项之外用自然语言文本回答的兜底入口.
 *
 * 两种使用场景共用同一份 UI(白底卡片 + 左侧编号方块 + "手动输入" 标题 +
 * 内嵌 textarea):
 *
 * 1. **single_choice 内嵌**(radio 模式):作为 augmentedOptions 末尾的虚拟
 *    选项,跟普通选项一起进 radio group.传 `radio` prop,渲染 input[type=radio]
 *    供选中切换;`expanded` 由父组件按 checked 状态控制.
 *
 * 2. **其他题型主体后追加**(独立块):multi_choice / picker / date_range /
 *    confirm 主 UI 下方一直显示这个组件,不需要 radio 切换(用户主动填了文本
 *    就生效).不传 `radio` prop,直接渲染 div + 始终展开 textarea.
 *
 * 外层固定用 .aiClerk-ua__radio + .aiClerk-ua__radio--manual class — 跟普通
 * 选项视觉完全一致(序号 counter / hover 行为 SCSS 自动适配).
 */
const ManualInputOption: React.FC<{
  /** textarea 当前文本 */
  value: string
  /** 文本变化 */
  onChange: (text: string) => void
  /** 是否展开输入区 — single_choice 由 checked 控制;其他题型默认 true 一直展开 */
  expanded?: boolean
  /** 是否禁用 */
  disabled?: boolean
  /** textarea 提示文案 */
  placeholder?: string
  /** 独立块模式：用户点击/激活「手动输入」时回调，让父组件感知"走了手动路径"，
   *  据此把互斥的主选项(如 picker「全部」)取消选中。 */
  onActivate?: () => void
  /** ===== radio 模式(仅 single_choice 用) ===== */
  radio?: {
    name: string
    optionValue: string
    checked: boolean
    onSelect: () => void
  }
}> = ({
  value,
  onChange,
  expanded: expandedProp,
  disabled,
  placeholder = '请输入...',
  onActivate,
  radio
}) => {
  // 独立块模式下用 internal state 控制 textarea 展开:
  //   - 默认收起(只显示 icon + "手动输入" 标题,跟其他选项视觉一致)
  //   - 用户点击卡片 → 展开 textarea + autoFocus
  //   - 用户填了文本(value 非空)→ 强制展开(防止误收起后看不到已填内容)
  // radio 模式下用外部 expandedProp 控制(由 checked 状态决定).
  const [internalExpanded, setInternalExpanded] = useState(false)
  const showInput = radio
    ? !!expandedProp
    : internalExpanded || value.length > 0
  const inputBlock = showInput ? (
    <div
      className="aiClerk-ua__expansion-wrap"
      onClick={(e) => e.stopPropagation()}
    >
      <textarea
        className="aiClerk-ua__custom-input aiClerk-ua__custom-input--multiline"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus
        rows={2}
      />
    </div>
  ) : null

  // 左侧固定铅笔 icon(不参与数字编号),所有模式共用.
  const iconNode = (
    <span className="aiClerk-ua__radio-icon" aria-hidden>
      <EditOutlined />
    </span>
  )

  // radio 模式:外层是 label,内含 input radio,可点击切换 checked.
  if (radio) {
    return (
      <label
        className={`aiClerk-ua__radio aiClerk-ua__radio--manual${
          radio.checked ? ' is-checked' : ''
        }`}
      >
        {iconNode}
        <input
          type="radio"
          name={radio.name}
          value={radio.optionValue}
          checked={radio.checked}
          onChange={radio.onSelect}
          disabled={disabled}
        />
        <span className="aiClerk-ua__radio-text">手动输入</span>
        {inputBlock}
      </label>
    )
  }
  // 独立块模式:外层 div,无 radio 行为.点击卡片展开 textarea(默认收起).
  return (
    <div
      className={`aiClerk-ua__radio aiClerk-ua__radio--manual${
        showInput ? ' is-checked' : ''
      }`}
      onClick={() => {
        setInternalExpanded(true)
        if (onActivate) onActivate()
      }}
      role="button"
      tabIndex={0}
    >
      {iconNode}
      <span className="aiClerk-ua__radio-text">手动输入</span>
      {inputBlock}
    </div>
  )
}

const QuestionInput: React.FC<{
  question: UserAskQuestion
  value: any
  customText: string
  expansionValue: any
  onChange: (value: any) => void
  onCustomChange: (text: string) => void
  onExpansionChange: (value: any) => void
  disabled: boolean
  /** 是否多题(part.questions.length > 1)：单题一律不预选(让用户主动选)，
   *  多题才启用「没 default 兜底选第一个」。透传给 getEffectiveDefault。 */
  multiQuestion: boolean
  /** 题型主体 UI 之外的"手动输入"兜底文本.对 multi_choice/keyword_select/
   *  picker/date_range/confirm 题在主 UI 下方多渲染一个 textarea,用户填了
   *  之后 resolveAnswer 按题型转换 wire 形态(数组项 / PickerAnswerValue / etc). */
  manualInput?: string
  onManualChange?: (text: string) => void
  /** picker 题"主动点过全部"的持久化 flag(按 qid,父组件存),透传给 PickerField。 */
  pickerAllConfirmed?: boolean
  onPickerAllConfirmedChange?: (v: boolean) => void
  /** picker 题用户点了「全部水印/全部成员」时回调 → 父组件触发自动跳。
   *  末题应传 undefined(末题不自动跳，只剩「提交」按钮)。 */
  onPickerAllSelected?: () => void
}> = ({
  question,
  value,
  customText,
  expansionValue,
  onChange,
  onCustomChange,
  onExpansionChange,
  disabled,
  multiQuestion,
  manualInput = '',
  onManualChange,
  pickerAllConfirmed,
  onPickerAllConfirmedChange,
  onPickerAllSelected
}) => {
  const userTouched = value !== undefined
  // date_range / picker 用：选了「主输入」(日期 / 全部水印 / 选具体项)时 bump 这个 key，
  // 重挂「手动输入」块、把它内部的展开/高亮态复位。二者互斥(manual 文本会覆盖主输入)，
  // 不收起会变成「主输入已选 + 手动输入还亮着」两个都选中的错觉。
  const [manualResetKey, setManualResetKey] = useState(0)
  // 用户是否走了「手动输入」路径(点过手动块)。用于把互斥的 picker「全部」取消选中。
  const [manualActive, setManualActive] = useState(false)
  // 手动路径是否激活：点过手动 或 已填文本(回看历史时 manualInput 非空也算)。
  const manualPathActive = manualActive || manualInput.trim().length > 0
  // 主输入变化(picker/日期) → 清空并收起「手动输入」+ 取消手动激活态(二者互斥)，
  // bump key 重挂复位收起。
  const resetManualBlock = () => {
    if (onManualChange && manualInput) onManualChange('')
    setManualActive(false)
    setManualResetKey((k) => k + 1)
  }

  // 题型主体下方追加的"手动输入"区域 — 给 multi_choice/keyword_select/picker/
  // date_range/confirm 题用,让用户填自然语言文本作为兜底答案.
  // 视觉直接复用 single_choice 的 __radio--manual 虚拟选项样式 — 选项卡白底
  // + 左侧铅笔 icon + 文字"手动输入" + 内嵌 textarea(放在 expansion-wrap 内,
  // flex-wrap 让它换到下一行).跟 single_choice 的 manual 选项视觉完全一致,
  // 不因题型不同而变成另一种"独立浅灰块".
  // single_choice 走原虚拟选项流程(MANUAL_DATE_VALUE),不渲染这个块.
  // text/person_select 本身就是文本输入,不重复加.
  const manualBlockNode = onManualChange ? (
    // key 含 qid：跨题切换天然重挂(各题独立)；含 manualResetKey：date_range/picker 选了
    // 主输入时 bump → 重挂复位收起(见 resetManualBlock)。multi/keyword 不 bump，手动是
    // 叠加项不受影响。
    <ManualInputOption
      key={`${question.qid}-${manualResetKey}`}
      value={manualInput}
      onChange={onManualChange}
      onActivate={() => {
        setManualActive(true)
        // picker / date_range：手动与主输入互斥 → 切到手动就清掉主输入已选的具体值
        //（全部水印 / 空日期是默认值 value===undefined，不用清）。用 raw onChange，不走
        // PickerField/date 那层 resetManualBlock 包装(否则会把刚激活的手动又关掉)。
        // multi / keyword / confirm 的手动是叠加项，不清。
        if (
          (question.type === 'picker' || question.type === 'date_range') &&
          value !== undefined
        ) {
          onChange(undefined)
        }
      }}
      disabled={disabled}
    />
  ) : null

  switch (question.type) {
    case 'single_choice': {
      const customSelected = value === CUSTOM_OPTION_VALUE
      const rawOptions = question.options || []
      // 后端已去掉"default 必须排 options[0]"校验 (openclaw validateRecommendedFirst),
      // 改由前端把 AI 推荐项 (value===default) 移到第一位显示 —— "AI 推荐" 徽标在最顶,
      // 用户第一眼看到默认建议, 决策路径最短. LLM 给的 options 顺序随意.
      const _defVal =
        typeof question.default === 'string' ? question.default : undefined
      const _defIdx = _defVal
        ? rawOptions.findIndex((o) => o.value === _defVal)
        : -1
      const baseOptions =
        _defIdx > 0
          ? [
              rawOptions[_defIdx],
              ...rawOptions.slice(0, _defIdx),
              ...rawOptions.slice(_defIdx + 1)
            ]
          : rawOptions
      // 默认选中的「有效默认值」：有 question.default 用它，没有则首选项。
      const effDefault = getEffectiveDefault(question, multiQuestion)
      // 自动追加"手动输入"虚拟选项的判定:
      //   - 题里已经有 expands='text' / 'date_range' 任意一种 → 不加
      //     (LLM 给的"自定义"/"其他"+ 输入控件已经能填自由值, 跟"手动输入"语义重复;
      //      用户看见 2 个不同入口会卡住认知)
      //   - 否则 → 加, 作为自由文本兜底入口
      // 仍然识别 date_range expansion option(典型"自定义时间"), 后续 placeholder
      // 文案和 wire 解析(parseDateRange)按这个 flag 分流.
      const hasDateRangeOption = baseOptions.some(
        (o) => inferExpansion(o, question) === 'date_range'
      )
      const hasTextExpansionOption = baseOptions.some(
        (o) => inferExpansion(o, question) === 'text'
      )
      const augmentedOptions =
        hasTextExpansionOption || hasDateRangeOption
          ? baseOptions
          : [...baseOptions, { value: MANUAL_DATE_VALUE, label: '手动输入' }]
      // 取当前选中项的 reason (chip 内不再单独显示, 统一放在 chip 列下方一行)
      const currentChecked = baseOptions.find((o) =>
        userTouched ? o.value === value : o.value === effDefault
      )
      const currentReason =
        currentChecked?.reason && currentChecked.reason.trim() !== 'AI 推荐'
          ? currentChecked.reason
          : undefined
      return (
        <div className="aiClerk-ua__radios">
          {augmentedOptions.map((opt) => {
            // 默认选中「有效默认值」对应项；用户动过后锁定到用户值。
            // 不再渲染「AI 推荐」标签 —— 推荐项直接以选中态(is-checked)呈现。
            const checked = userTouched
              ? value === opt.value
              : opt.value === effDefault
            const expansion = inferExpansion(opt, question)
            const isManualOpt = opt.value === MANUAL_DATE_VALUE
            // manual 虚拟选项走 ManualInputOption 共享组件(radio 模式),
            // 跟其他题型的独立块 manual 形态完全一致.
            if (isManualOpt) {
              return (
                <ManualInputOption
                  key={opt.value}
                  value={
                    typeof expansionValue === 'string' ? expansionValue : ''
                  }
                  onChange={onExpansionChange}
                  expanded={checked}
                  disabled={disabled}
                  placeholder={
                    hasDateRangeOption ? '请输入数据源的时间范围' : '请输入...'
                  }
                  radio={{
                    name: question.qid,
                    optionValue: opt.value,
                    checked,
                    onSelect: () => onChange(opt.value)
                  }}
                />
              )
            }
            return (
              <React.Fragment key={opt.value}>
                <label
                  className={`aiClerk-ua__radio${checked ? ' is-checked' : ''}`}
                  onClick={() => {
                    // 点「已选中」项时 radio 的 onChange 不会 fire(无状态变化)，补一发
                    // 让 updateAnswer 能据此自动跳下一个；未选中项的点击由下方 input
                    // onChange 处理(此处 checked=false 不重复触发)。
                    if (checked) onChange(opt.value)
                  }}
                >
                  <input
                    type="radio"
                    name={question.qid}
                    value={opt.value}
                    checked={checked}
                    onChange={() => {
                      // 时间范围 chip 命中关键词时按 spec 上报 time_range_*;
                      // 题目不是时间范围 / 选项不匹配 → 不上报(spec 未覆盖)
                      const ctx = `${question.question} ${question.hint || ''}`
                      if (DATE_CONTEXT_RE.test(ctx)) {
                        const item = pickTimeRangeChipTrackItem(opt)
                        if (item) {
                          // spec: time_range_* 不带 string1
                          trackClick('formFilling', item, {
                            requirement_id: 1103,
                            track_sign:
                              'aai.b8137.click.ai_click_web_ai_form_filling',
                            sessionID: readSessionId()
                          })
                        }
                      }
                      onChange(opt.value)
                    }}
                    disabled={disabled}
                  />
                  <span className="aiClerk-ua__radio-text">
                    {opt.label || opt.value}
                  </span>
                  {/* expansion 区域放在 label 内部最后,跟选项文字成一个整体
                      "选项 + 答案"在视觉上属于同一卡片.label 自带 padding-left: 56,
                      子元素自然从选项文字位置起始,无需额外缩进.
                      flex-wrap: wrap + expansion-wrap flex-basis 100% 让它换到下一行. */}
                  {checked && expansion === 'text' && (
                    <div className="aiClerk-ua__expansion-wrap">
                      <textarea
                        className="aiClerk-ua__custom-input aiClerk-ua__custom-input--multiline"
                        placeholder="请输入..."
                        value={
                          typeof expansionValue === 'string'
                            ? expansionValue
                            : ''
                        }
                        onChange={(e) => onExpansionChange(e.target.value)}
                        // 阻止 textarea 点击冒泡到 label,避免触发 radio re-click
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                        autoFocus
                        rows={2}
                      />
                    </div>
                  )}
                  {checked && expansion === 'date_range' && (
                    <div
                      className="aiClerk-ua__expansion-wrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="aiClerk-ua__expansion">
                        <DateRangeInput
                          value={expansionValue}
                          onChange={onExpansionChange}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  )}
                </label>
              </React.Fragment>
            )
          })}
          {question.allow_custom && (
            <label
              className={`aiClerk-ua__radio${
                customSelected ? ' is-checked' : ''
              }`}
            >
              <input
                type="radio"
                name={question.qid}
                checked={customSelected}
                onChange={() => onChange(CUSTOM_OPTION_VALUE)}
                disabled={disabled}
              />
              <span className="aiClerk-ua__radio-text">其他（自己填）</span>
            </label>
          )}
          {question.allow_custom && customSelected && (
            <input
              type="text"
              className="aiClerk-ua__custom-input"
              placeholder="请输入..."
              value={customText}
              onChange={(e) => onCustomChange(e.target.value)}
              disabled={disabled}
              autoFocus
            />
          )}
          {/* 选中项的 reason 单独一行,放 chip 列下方,不挤进 chip 里 */}
          {currentReason && (
            <div className="aiClerk-ua__chip-reason">{currentReason}</div>
          )}
        </div>
      )
    }

    case 'multi_choice':
    case 'keyword_select': {
      // 默认预选「有效默认值」：有 question.default(数组)用它，没有则 [首选项]。
      // 用户没动过时直接展示为预选状态。
      const eff = getEffectiveDefault(question, multiQuestion)
      const fallback: string[] = !userTouched && Array.isArray(eff) ? eff : []
      const selected: string[] = Array.isArray(value)
        ? value
        : userTouched
          ? []
          : fallback
      const toggle = (v: string) => {
        const next = selected.includes(v)
          ? selected.filter((s) => s !== v)
          : [...selected, v]
        onChange(next)
      }
      return (
        <>
          <div className="aiClerk-ua__checks">
            {(question.options || []).map((opt) => {
              const isSelected = selected.includes(opt.value)
              return (
                <label
                  className={`aiClerk-ua__check${
                    isSelected ? ' is-checked' : ''
                  }`}
                  key={opt.value}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(opt.value)}
                    disabled={disabled}
                  />
                  <span className="aiClerk-ua__check-text">
                    {opt.label || opt.value}
                  </span>
                </label>
              )
            })}
            {question.allow_custom && (
              <input
                type="text"
                className="aiClerk-ua__custom-input"
                placeholder="自定义..."
                value={customText}
                onChange={(e) => onCustomChange(e.target.value)}
                disabled={disabled}
              />
            )}
          </div>
          {manualBlockNode}
        </>
      )
    }

    case 'date_range':
      return (
        <>
          <DateRangeInput
            value={value}
            onChange={(v) => {
              // 改日期 = 选「按日期」：清空并收起「手动输入」(二者互斥，manual 文本会覆盖日期)
              resetManualBlock()
              onChange(v)
            }}
            disabled={disabled}
          />
          {manualBlockNode}
        </>
      )

    case 'text':
      return (
        <textarea
          className="aiClerk-ua__textarea"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.hint || '请输入...'}
          disabled={disabled}
          rows={3}
        />
      )

    case 'confirm': {
      // 二元拍板: 双按钮【是】【否】, 点击即触发 onChange, 父组件 useEffect 监听 answer 变化立即 submit.
      // 默认高亮「有效默认值」：有 question.default 用它，没有默认选「是」(第一个按钮)。
      // userTouched 后锁定到用户值。不再渲染「AI 推荐」标签，直接以选中态呈现。
      const eff = getEffectiveDefault(question, multiQuestion)
      const effYes = eff === true
      const effNo = eff === false
      return (
        <>
          <div className="aiClerk-ua__confirm-buttons">
            <button
              type="button"
              className={`aiClerk-ua__confirm-btn${
                (userTouched && value === true) || (!userTouched && effYes)
                  ? ' is-selected'
                  : ''
              }`}
              onClick={() => onChange(true)}
              disabled={disabled}
            >
              是
            </button>
            <button
              type="button"
              className={`aiClerk-ua__confirm-btn${
                (userTouched && value === false) || (!userTouched && effNo)
                  ? ' is-selected'
                  : ''
              }`}
              onClick={() => onChange(false)}
              disabled={disabled}
            >
              否
            </button>
          </div>
          {manualBlockNode}
        </>
      )
    }

    case 'picker':
      return (
        <>
          <PickerField
            question={question}
            value={value}
            onChange={(v) => {
              // 选了 picker(全部水印 / 具体项) = 选「按筛选」：清空并收起「手动输入」
              // (二者互斥，manual 文本会覆盖 picker 选择)
              resetManualBlock()
              onChange(v)
            }}
            disabled={disabled}
            multiQuestion={multiQuestion}
            manualActive={manualPathActive}
            confirmedAll={pickerAllConfirmed}
            onConfirmedAllChange={onPickerAllConfirmedChange}
            onPickerStart={resetManualBlock}
            onAllSelected={onPickerAllSelected}
          />
          {manualBlockNode}
        </>
      )

    case 'person_select':
      return (
        <input
          type="text"
          className="aiClerk-ua__person-input"
          value={
            typeof value === 'string'
              ? value
              : Array.isArray(value)
                ? value.join(', ')
                : ''
          }
          onChange={(e) =>
            onChange(
              e.target.value
                .split(/[,，]/)
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          placeholder="输入人员姓名，多人用逗号分隔"
          disabled={disabled}
        />
      )

    default:
      return null
  }
}

/**
 * PickerField — type='picker' 的渲染:chip 列 + Modal 按钮.
 *
 * 复用项目既有的 DepartmentMemberPickerV2 / WatermarksPicker, 让用户从全集
 * 挑选成员/部门/水印. 协议层 value 形态 = PickerAnswerValue (D 方案,
 * ids: string[] + summary 字符串).
 *
 * **分层说明** (避免后人误解):
 *   - 协议 state (`value`): 进 answers, 提交后序列化进 [user_ask_response].
 *     形态瘦 —— 只有 ids 数组 + summary 字符串.
 *   - UI state (`localUsers/localDepts/localWatermarks`): 完整对象, 仅供
 *     chip 列展示 name 用. **不进协议**. 用户切走再切回保留 (React state),
 *     刷新页面会丢, 这时 chip 列退化为单一 summary chip.
 *
 * 水印 picker 的 onOK 只返回 id 字符串数组, 不带 name. 这里走"合并成单 chip
 * 显示 summary" 的退化形态; 成员 picker 的 onOk 带完整对象, chip 列能逐个展开.
 */
const PickerField: React.FC<{
  question: UserAskQuestion
  value: any
  onChange: (value: any) => void
  disabled: boolean
  /** 多题时：picker「全部」默认做成已选中(实心)，跟选项题预选第一个对齐；
   *  单题不预选(保持虚线默认态)。没传 → 视作 false。 */
  multiQuestion?: boolean
  /** 「手动输入」路径已激活时，「全部」不显示选中态(二者互斥，避免两个都选中的错觉)。 */
  manualActive?: boolean
  /** 用户是否主动点过"全部成员/全部水印"(由父组件按 qid 持久化,跨翻页保持)。
   *  没传(如 list 模式)→ 视作 false,base 样式本就实心高亮,不影响。 */
  confirmedAll?: boolean
  onConfirmedAllChange?: (v: boolean) => void
  /** 用户开始走 picker 路径(点「选择」按钮)时回调 → 父组件收起互斥的「手动输入」。 */
  onPickerStart?: () => void
  /** 用户点了「全部水印/全部成员」(picker-all 按钮)时回调 → 父组件触发自动跳下一题。
   *  跟 onChange(undefined) 是不同的语义信号：onChange(undefined) 也可能来自
   *  ManualInputOption.onActivate(切到手动 → 清主输入)，不该自动跳；这个回调只在
   *  用户主动确认"查全部"时触发。 */
  onAllSelected?: () => void
}> = ({
  question,
  value,
  onChange,
  disabled,
  multiQuestion = false,
  manualActive = false,
  confirmedAll = false,
  onConfirmedAllChange,
  onPickerStart,
  onAllSelected
}) => {
  const dispatch = useAppDispatch()
  const kind: 'department_member' | 'watermark' =
    question.picker?.kind === 'watermark' ? 'watermark' : 'department_member'
  const [visible, setVisible] = useState(false)

  // 在 UserAskActionSheet 内渲染时, picker visible 上报给外层 ActionSheet
  // → ActionSheet 隐藏自己, 避免多层弹窗叠加.
  // chat 流 inline 模式没有 Context (Context value 为 null), useEffect 跳过.
  const sheetPickerCtx = useContext(ActionSheetPickerContext)
  /** Picker 视觉类型, 上报埋点用 (members / watermarks).
   *  跟 question.picker.kind 一一对应,但前端埋点统一用 spec 的 members/watermarks 词. */
  const reportKind: 'members' | 'watermarks' =
    kind === 'watermark' ? 'watermarks' : 'members'
  /** visible 上次值,用来侦测 true→false 但缺少关闭原因的兜底 (用户点 mask 关 Modal) */
  const prevVisibleRef = useRef(false)
  useEffect(() => {
    if (sheetPickerCtx) {
      sheetPickerCtx.setPickerOpen(visible)
      // 同步上报 picker open 埋点;close 走 onOk/onCancel,这里不重报
      if (visible && !prevVisibleRef.current) {
        sheetPickerCtx.reportPickerOpen(reportKind)
      }
    }
    prevVisibleRef.current = visible
  }, [visible, sheetPickerCtx, reportKind])
  // UI state: 完整对象, 用于 chip 列 name 回显. 不进协议.
  const [localUsers, setLocalUsers] = useState<any[]>([])
  const [localDepts, setLocalDepts] = useState<any[]>([])
  const [localWatermarks, setLocalWatermarks] = useState<
    Array<{ id: string; name: string }>
  >([])
  // confirmedAll 由父组件按 qid 持久化(见 props);改动时直接调 onConfirmedAllChange,
  // list 模式没传则 optional chaining 跳过(base 样式不依赖它)。

  // department_member 摘要拼接 —— 部门在前 + 成员在后, 各取前 N 项展开, 余下计数
  const buildMemberSummary = useCallback(
    (users: any[], depts: any[]): string => {
      const deptHead = depts
        .slice(0, 2)
        .map((d) => d.name)
        .join('、')
      const userHead = users
        .slice(0, 3)
        .map((u) => u.name)
        .join('、')
      const parts: string[] = []
      if (depts.length > 0) {
        parts.push(
          deptHead +
            (depts.length > 2 ? ` 等 ${depts.length} 个部门` : '(部门)')
        )
      }
      if (users.length > 0) {
        parts.push(
          userHead + (users.length > 3 ? ` 等 ${users.length} 人` : '')
        )
      }
      return parts.join(' + ') || '未选择'
    },
    []
  )

  const buildWatermarkSummary = useCallback(
    (items: Array<{ id: string; name: string }>): string => {
      if (items.length === 0) return '未选择'
      const head = items
        .slice(0, 3)
        .map((x) => x.name)
        .join('、')
      return items.length > 3 ? `${head} 等 ${items.length} 个水印` : head
    },
    []
  )

  const rebuildMemberValue = useCallback(
    (users: any[], depts: any[]) => {
      if (users.length === 0 && depts.length === 0) {
        onChange(undefined)
        return
      }
      const val: PickerAnswerValue = {
        kind: 'department_member',
        user_ids: users.map((u) => u.userID).filter(Boolean),
        department_ids: depts.map((d) => d.id).filter(Boolean),
        summary: buildMemberSummary(users, depts)
      }
      onChange(val)
    },
    [onChange, buildMemberSummary]
  )

  const handleMemberOk = useCallback(
    (params: {
      selectedDepartmentUsers: any[]
      selectedDepartments: any[]
    }) => {
      const { selectedDepartmentUsers, selectedDepartments } = params
      // 走具体选择路径 → 取消"主动选全部"标记(否则清空后会错误停留在选中态)
      if (onConfirmedAllChange) onConfirmedAllChange(false)
      setLocalUsers(selectedDepartmentUsers)
      setLocalDepts(selectedDepartments)
      rebuildMemberValue(selectedDepartmentUsers, selectedDepartments)
      if (sheetPickerCtx)
        sheetPickerCtx.reportPickerClose(reportKind, 'confirm')
      setVisible(false)
    },
    [rebuildMemberValue, onConfirmedAllChange, sheetPickerCtx, reportKind]
  )

  const handleWatermarkOk = useCallback(
    (checkedList: string[]) => {
      if (sheetPickerCtx) {
        sheetPickerCtx.reportPickerClose(reportKind, 'confirm')
      }
      setVisible(false)
      // 走具体选择路径 → 取消"主动选全部"标记
      if (onConfirmedAllChange) onConfirmedAllChange(false)
      // WatermarksPicker 在"全选"时也会 onOK([]) 当作清空语义, 跟这里期望一致.
      if (!checkedList || checkedList.length === 0) {
        setLocalWatermarks([])
        onChange(undefined)
        return
      }
      // onOK 只给 id, 不给 name. 退化: 用 id 当 name 占位, summary 写"已选 N 项",
      // 待用户重开 picker 时再看. 这里只为 chip 列能立刻显示.
      const items = checkedList.map((id) => ({
        id,
        name: `水印 ${id.slice(0, 6)}`
      }))
      setLocalWatermarks(items)
      const val: PickerAnswerValue = {
        kind: 'watermark',
        watermark_ids: checkedList,
        // 名字拿不到时, summary 退化为"已选 N 个水印", LLM 至少能看到数量
        summary: `已选 ${checkedList.length} 个水印`
      }
      onChange(val)
    },
    [onChange, onConfirmedAllChange, sheetPickerCtx, reportKind]
  )

  const removeUser = useCallback(
    (uid: string) => {
      const next = localUsers.filter((u) => u.userID !== uid)
      setLocalUsers(next)
      rebuildMemberValue(next, localDepts)
    },
    [localUsers, localDepts, rebuildMemberValue]
  )

  const removeDept = useCallback(
    (did: string) => {
      const next = localDepts.filter((d) => d.id !== did)
      setLocalDepts(next)
      rebuildMemberValue(localUsers, next)
    },
    [localUsers, localDepts, rebuildMemberValue]
  )

  const removeWatermark = useCallback(
    (wid: string) => {
      const next = localWatermarks.filter((w) => w.id !== wid)
      setLocalWatermarks(next)
      if (next.length === 0) {
        onChange(undefined)
        return
      }
      const val: PickerAnswerValue = {
        kind: 'watermark',
        watermark_ids: next.map((w) => w.id),
        summary: buildWatermarkSummary(next)
      }
      onChange(val)
    },
    [localWatermarks, onChange, buildWatermarkSummary]
  )

  // DepartmentMemberPickerV2 的 filterObj 回显数据, 关闭再开 picker 不丢预选
  const filterObj = useMemo(
    () => ({ departmentUsers: localUsers, departments: localDepts }),
    [localUsers, localDepts]
  )

  // WatermarksPicker 要求外部传入 fetchWatermarkList prop
  const fetchWatermarkListHandler = useCallback(
    (params: object) => dispatch(fetchWatermarkList(params)),
    [dispatch]
  )

  // 组装 chips 列表
  type Chip = { key: string; name: string; onRemove: () => void }
  const chips: Chip[] = []
  if (kind === 'department_member') {
    localDepts.forEach((d) =>
      chips.push({
        key: `dept-${d.id}`,
        name: d.name + '(部门)',
        onRemove: () => removeDept(d.id)
      })
    )
    localUsers.forEach((u) =>
      chips.push({
        key: `user-${u.userID}`,
        name: u.name,
        onRemove: () => removeUser(u.userID)
      })
    )
  } else {
    localWatermarks.forEach((w) =>
      chips.push({
        key: `wm-${w.id}`,
        name: w.name,
        onRemove: () => removeWatermark(w.id)
      })
    )
  }

  // 用户刷新过页面 / hydration 后 local state 是空, 但 value 还在 (协议序列化保留).
  // 这时 chip 列退化: 用 value.summary 显示成一个 chip, 整体可整组清空.
  const hasIds =
    value &&
    typeof value === 'object' &&
    ((Array.isArray(value.user_ids) && value.user_ids.length > 0) ||
      (Array.isArray(value.department_ids) &&
        value.department_ids.length > 0) ||
      (Array.isArray(value.watermark_ids) && value.watermark_ids.length > 0))
  if (chips.length === 0 && hasIds) {
    chips.push({
      key: 'summary',
      name: value.summary || '已选',
      onRemove: () => onChange(undefined)
    })
  }

  // 没选任何成员/水印 = 不加筛选条件 = 查全部 (后端拿到空 ids 天然查全集).
  // UI 上用一个灰色"全部"chip 把这个默认语义显式说出来, 避免用户以为"空着 =
  // 没选 = 查不到".
  const hasSelection = chips.length > 0
  const allLabel = kind === 'department_member' ? '全部成员' : '全部水印'
  // 总选中数 — 用于已选态文案兜底(summary 缺失时回退到"N 项").
  // 优先用 value 的 ids 长度,可靠不依赖 localUsers/Depts(刷新会丢)
  const selectedCount =
    (value && typeof value === 'object'
      ? (Array.isArray(value.user_ids) ? value.user_ids.length : 0) +
        (Array.isArray(value.department_ids)
          ? value.department_ids.length
          : 0) +
        (Array.isArray(value.watermark_ids) ? value.watermark_ids.length : 0)
      : 0) || chips.length
  // 已选态文案:用 value.summary 拼"已选(xxx 等 N 项)" — summary 已自带
  // 前 N 个名字 + 截断尾部("市场部、研发部 等 3 个部门 + 张三、李四 等 8 人"),
  // 这里直接拿来用,过长由 CSS ellipsis 末尾省略.
  const summaryText =
    (value && typeof value === 'object' && value.summary) ||
    `${selectedCount} 项`
  const addLabel = hasSelection
    ? `已选(${summaryText})`
    : kind === 'department_member'
      ? '+ 选择成员 / 部门'
      : '+ 选择水印'

  // "全部水印 / 全部成员" 点击 = 确认选「全部」(也用作清空已选回默认).
  // 不论 hasSelection 都触发 onChange(undefined) — 让父组件(step 模式)
  // 可以通过 updateAnswer 感知到用户主动点了"全部",触发 advance 到下一个.
  // 旧版有 hasSelection early return,导致默认态点击毫无反馈,用户以为坏了.
  const resetToAll = useCallback(() => {
    setLocalUsers([])
    setLocalDepts([])
    setLocalWatermarks([])
    if (onConfirmedAllChange) onConfirmedAllChange(true)
    onChange(undefined)
    // 触发父组件「点了全部 → 自动跳下一题」(末题时父组件会传 undefined 不跳)。
    // 必须先 onChange 让 answers 更新再 advance，否则跳过去后再回看本题状态丢失。
    if (onAllSelected) onAllSelected()
  }, [onChange, onConfirmedAllChange, onAllSelected])

  return (
    <div className="aiClerk-ua__picker">
      <div className="aiClerk-ua__picker-chips">
        {/* "全部水印 / 全部成员" 始终显示在最前 — 既是当前状态显示, 也是
            "一键回默认"按钮.disabled 态用 <span> 退化为只读. */}
        {disabled ? (
          <span className="aiClerk-ua__picker-all">{allLabel}</span>
        ) : (
          <button
            type="button"
            className={`aiClerk-ua__picker-all aiClerk-ua__picker-all--clickable${
              hasSelection
                ? ''
                : (confirmedAll || multiQuestion) && !manualActive
                  ? ' aiClerk-ua__picker-all--selected'
                  : ' aiClerk-ua__picker-all--active'
            }`}
            onClick={resetToAll}
            aria-label={hasSelection ? `清空选择,回到${allLabel}` : allLabel}
            aria-pressed={
              !hasSelection && (confirmedAll || multiQuestion) && !manualActive
            }
            title={hasSelection ? '点击回到默认(查全部)' : undefined}
          >
            {allLabel}
          </button>
        )}
        {/* 已选 chips, 跟"全部水印"chip 并列 */}
        {hasSelection &&
          chips.map((c) => (
            <span key={c.key} className="aiClerk-ua__picker-chip">
              <span className="aiClerk-ua__picker-chip-name">{c.name}</span>
              {!disabled && (
                <button
                  type="button"
                  className="aiClerk-ua__picker-chip-x"
                  onClick={c.onRemove}
                  aria-label="移除"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        {!disabled && (
          <button
            type="button"
            className={`aiClerk-ua__picker-add${
              hasSelection ? ' is-selected' : ''
            }`}
            onClick={() => {
              // 按 picker kind 区分埋点(成员/部门 vs 水印)
              const clickItem =
                kind === 'department_member'
                  ? 'select_member_or_dept'
                  : 'select_watermark'
              // spec: select_member_or_dept / select_watermark 不带 string1
              trackClick('formFilling', clickItem, {
                requirement_id: 1103,
                track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
                sessionID: readSessionId()
              })
              // 点「选择」= 走 picker 路径 → 收起互斥的「手动输入」
              if (onPickerStart) onPickerStart()
              setVisible(true)
            }}
            title={hasSelection ? addLabel : undefined}
          >
            {/* 包 span 让 CSS overflow:hidden + text-overflow:ellipsis 生效
                (button 是 flex 容器,直接 text-node 无法 ellipsis) */}
            <span className="aiClerk-ua__picker-add-text">{addLabel}</span>
          </button>
        )}
      </div>
      {/* DepartmentMemberPicker 内部硬编码 getContainer={false},Modal 在父 DOM
          内渲染 → 在 UserAskActionSheet 容器内会被 overflow:hidden 裁切.
          外包 createPortal 把它拉到 body 直接子节点,脱离 ActionSheet DOM 子树,
          这样配合 sheetPickerCtx.setPickerOpen() 让 ActionSheet 隐藏时 picker
          继续在 body 上独立显示. WatermarksPicker 已 portal 到 body,无需此处理. */}
      {kind === 'department_member' &&
        createPortal(
          <DepartmentMemberPicker
            open={visible}
            filterObj={filterObj}
            onOk={handleMemberOk}
            onCancel={() => {
              if (sheetPickerCtx) {
                sheetPickerCtx.reportPickerClose(reportKind, 'cancel')
              }
              setVisible(false)
            }}
          />,
          document.body
        )}
      {kind === 'watermark' && (
        <WatermarksPicker
          visible={visible}
          checkedList={(value && value.watermark_ids) || []}
          onOK={handleWatermarkOk}
          onCancel={() => {
            if (sheetPickerCtx) {
              sheetPickerCtx.reportPickerClose(reportKind, 'cancel')
            }
            setVisible(false)
          }}
          fetchWatermarkList={fetchWatermarkListHandler}
        />
      )}
    </div>
  )
}

/** date_range 的子组件:RangePicker(带 5 个快捷 ranges).
 *  自然语言输入由 single_choice 的"手动填写"虚拟选项独立承载,这里不再混在一起.
 */
const DateRangeInput: React.FC<{
  value: any
  onChange: (value: { start: string; end: string }) => void
  disabled: boolean
}> = ({ value, onChange, disabled }) => {
  const range =
    value && typeof value === 'object' ? value : { start: '', end: '' }

  // ranges 直接走 parseDateRange,不重复实现日期计算.
  // 这些 ranges 显示在 antd RangePicker 弹层底部,点击直接套用日期 — 用户不再
  // 需要"在外面"点快捷按钮,日历内一键完成.
  const SHORTCUT_KEYS = ['最近1周', '最近30天', '最近3个月', '本月', '上月']
  const SHORTCUT_LABELS: Record<string, string> = {
    最近1周: '最近 1 周',
    最近30天: '最近 30 天',
    最近3个月: '最近 3 个月',
    本月: '本月',
    上月: '上月'
  }
  const ranges = useMemo(() => {
    const r: Record<string, [dayjs.Dayjs, dayjs.Dayjs]> = {}
    SHORTCUT_KEYS.forEach((key) => {
      const res = parseDateRange(key)
      if (res) r[SHORTCUT_LABELS[key]] = [dayjs(res.start), dayjs(res.end)]
    })
    return r
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 把字符串日期转 [dayjs, dayjs] 给 RangePicker;反过来 onChange 转回 YYYY-MM-DD
  const rangePickerValue: [dayjs.Dayjs, dayjs.Dayjs] | null =
    range.start && range.end ? [dayjs(range.start), dayjs(range.end)] : null
  const handlePickerChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    if (!dates || !dates[0] || !dates[1]) {
      onChange({ start: '', end: '' })
      return
    }
    onChange({
      start: dates[0].format('YYYY-MM-DD'),
      end: dates[1].format('YYYY-MM-DD')
    })
  }

  return (
    <div className="aiClerk-ua__date-wrap">
      <DatePicker.RangePicker
        className="aiClerk-ua__range-picker"
        value={rangePickerValue as any}
        onChange={handlePickerChange as any}
        ranges={ranges as any}
        format="YYYY-MM-DD"
        disabled={disabled}
        allowClear
      />
    </div>
  )
}

export default UserAskBlock
